import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Plus, Calendar, Image as ImageIcon, ExternalLink, LogOut, Trash2, Clock, AlertCircle, ShieldCheck } from "lucide-react";
import { getExpiryStatus } from "../components/ExpiryBanner";
import DriveConnectCard from "../components/DriveConnectCard";
import ReferralCard from "../components/ReferralCard";
import UnclaimedCreditsBanner from "../components/UnclaimedCreditsBanner";
import SuperAlbumsPanel from "../components/SuperAlbumsPanel";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminStatus, setAdminStatus] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get("/events");
      setEvents(res.data);
    } catch (error) {
      console.error("Failed to load events:", error);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/status").then((r) => setAdminStatus(r.data)).catch(() => {});
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this event and all uploads? This cannot be undone.")) return;
    await api.delete(`/events/${id}`);
    setEvents(events.filter(e => e.id !== id));
  };

  return (
    <section className="min-h-[85vh] bg-cream-100 py-12">
      <div className="max-w-6xl mx-auto px-5">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Host Dashboard</p>
            <h1 className="font-serif text-navy-700 text-4xl md:text-5xl mt-2">Hi, <span className="italic">{user?.name?.split(" ")[0] || "there"}</span></h1>
          </div>
          <div className="flex gap-3">
            {adminStatus && (adminStatus.is_admin || !adminStatus.admin_exists) && (
              <Link to="/dashboard/admin" data-testid="admin-link" className="inline-flex items-center gap-2 border border-navy-700/20 text-navy-700 px-5 py-2.5 rounded-full font-semibold hover:bg-white"><ShieldCheck className="w-4 h-4" /> Admin</Link>
            )}
            <Link to="/dashboard/new" className="inline-flex items-center gap-2 bg-coral-300 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-coral-400"><Plus className="w-4 h-4" /> New event</Link>
            <button onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-2 border border-navy-700/20 text-navy-700 px-5 py-2.5 rounded-full font-semibold hover:bg-white"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>

        {/* Google Drive connect */}
        <div className="mb-8 space-y-4">
          <UnclaimedCreditsBanner />
          <DriveConnectCard />
          <ReferralCard />
        </div>

        {loading ? (
          <div className="text-navy-700/60">Loading events...</div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center soft-shadow">
            <h2 className="font-serif italic text-navy-700 text-3xl">No events yet</h2>
            <p className="text-navy-700/70 mt-3">Create your first event in under 2 minutes.</p>
            <Link to="/dashboard/new" className="mt-6 inline-flex items-center gap-2 bg-coral-300 text-white px-6 py-3 rounded-full font-semibold hover:bg-coral-400">Create event</Link>
          </div>
        ) : (
          <>
            {/* Reminders summary */}
            {(() => {
              const expiring = events
                .map(e => ({ event: e, ...getExpiryStatus(e.expires_at, e.tier) }))
                .filter(r => r.status !== "safe");
              if (expiring.length === 0) return null;
              return (
                <div className="mb-8 bg-amber-50 border border-amber-200 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-amber-700" />
                    <h2 className="font-semibold text-amber-900">
                      {expiring.length} event{expiring.length === 1 ? "" : "s"} need{expiring.length === 1 ? "s" : ""} your attention
                    </h2>
                  </div>
                  <div className="space-y-2">
                    {expiring.slice(0, 3).map(({ event, daysLeft, status }) => (
                      <div key={event.id} className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                          <span className="font-medium text-navy-700 truncate">{event.name}</span>
                          <span className="text-sm text-navy-700/60">
                            {status === "expired" ? "Expired" : status === "critical" ? "<1 day left" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
                          </span>
                        </div>
                        <Link to={`/dashboard/events/${event.id}/upgrade`} className="text-sm bg-coral-300 text-white px-4 py-1.5 rounded-md font-semibold hover:bg-coral-400">
                          Upgrade
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((e) => (
              <div key={e.id} className="bg-white rounded-2xl overflow-hidden soft-shadow group">
                <div className="aspect-[16/9] bg-cream-200 relative">
                  {e.cover_image ? (
                    <img src={e.cover_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-coral-100 to-cream-200">
                      <ImageIcon className="w-10 h-10 text-coral-300/60" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2 py-1 rounded text-[10px] uppercase font-bold tracking-wider text-coral-500">{e.type}</div>
                  {e.sponsored && (
                    <div data-testid="dashboard-sponsored-badge" className="absolute top-3 right-3 bg-coral-500 text-white px-2 py-1 rounded text-[10px] uppercase font-bold tracking-wider inline-flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Sponsored
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-serif italic text-navy-700 text-2xl">{e.name}</h3>
                  {e.date && <div className="text-sm text-navy-700/60 mt-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {e.date}</div>}
                  <div className="mt-3 text-sm text-navy-700/70">{e.upload_count} uploads</div>
                  {(() => {
                    const exp = e.expires_at ? new Date(e.expires_at) : null;
                    if (!exp || Number.isNaN(+exp)) return null;
                    const diffDays = (exp.getTime() - Date.now()) / 86400000;
                    const expired = diffDays <= 0;
                    const soon = diffDays > 0 && diffDays <= 14;
                    if (!expired && !soon) return null;
                    const days = Math.ceil(Math.abs(diffDays));
                    return (
                      <Link
                        to={`/dashboard/events/${e.id}/upgrade?mode=renew`}
                        data-testid={`dashboard-renew-${e.id}`}
                        className={`mt-3 inline-flex items-center gap-1.5 w-full justify-center px-3 py-1.5 rounded-full text-xs font-bold ${
                          expired
                            ? "bg-red-500 text-white hover:bg-red-600"
                            : diffDays <= 3
                              ? "bg-coral-400 text-white hover:bg-coral-500"
                              : "bg-coral-100 text-coral-600 border border-coral-200 hover:bg-coral-200"
                        }`}
                      >
                        {expired
                          ? `Expired ${days}d ago — Renew & Pay`
                          : `Expires in ${days}d — Renew & Pay`}
                      </Link>
                    );
                  })()}
                  <div className="mt-5 flex gap-2">
                    <Link to={`/dashboard/events/${e.id}`} className="flex-1 text-center bg-coral-300 text-white py-2 rounded-md text-sm font-semibold hover:bg-coral-400">Manage</Link>
                    <a href={`/g/${e.slug}`} target="_blank" rel="noreferrer" className="px-3 py-2 border border-navy-700/20 rounded-md text-navy-700 hover:bg-cream-100" title="Open guest view"><ExternalLink className="w-4 h-4" /></a>
                    <button onClick={() => remove(e.id)} className="px-3 py-2 border border-red-200 text-red-500 rounded-md hover:bg-red-50" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-10">
          <SuperAlbumsPanel />
        </div>

      </div>
    </section>
  );
};

export default Dashboard;
